import type { DataSource as DriverSource } from '@casa/driver-sdk';
import { tilesOf, type Dashboard, type Tile } from '@casa/model';

// The data sources of the dashboard, as the drivers see them: one per named source of each tile,
// with the id `<tile id>/<source name>`, unique in the dashboard (a dashboard reuses source names
// across tiles, such as the Solar screen's `today` and `yesterday`).

/** One data source of one tile, bound to one connection. */
export interface SourceRef {
    readonly connection: string;
    /** What the driver sees: the source's params, its refresh interval, and a unique id. */
    readonly source: DriverSource;
}

export interface TileSources {
    readonly byName: ReadonlyMap<string, SourceRef>;
    readonly all: readonly SourceRef[];
}

export interface DashboardSources {
    /** By tile id, so that a tile moved in edit mode (a new object) keeps its sources. */
    readonly tiles: ReadonlyMap<string, TileSources>;
    readonly byConnection: ReadonlyMap<string, readonly DriverSource[]>;
}

export function collectSources(dashboard: Dashboard) : DashboardSources {
    const tiles = new Map<string, TileSources>();
    const byConnection = new Map<string, DriverSource[]>();

    for (const screen of dashboard.screens) {
        for (const tile of tilesOf(screen)) {
            const byName = new Map<string, SourceRef>();
            for (const [name, definition] of Object.entries(tile.dataSources)) {
                const source: DriverSource = {
                    ...definition.params,
                    id: `${tile.id}/${name}`,
                    ...(definition.refreshInterval !== undefined ? { refreshInterval: definition.refreshInterval } : {}),
                };
                byName.set(name, { connection: definition.connection, source });
                const list = byConnection.get(definition.connection) ?? [];
                list.push(source);
                byConnection.set(definition.connection, list);
            }
            tiles.set(tile.id, { byName, all: [...byName.values()] });
        }
    }

    return { tiles, byConnection };
}

/** The tile's source with this name; undefined for no name or an unknown one. */
export function resolveSource(sources: TileSources | undefined, name: string | undefined) : SourceRef | undefined {
    return name === undefined ? undefined : sources?.byName.get(name);
}
